import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card, Table, Tabs, Tag, Button, Space, Rate, message, Popconfirm, Switch, Typography
} from 'antd';
import {
  StarOutlined, CheckOutlined, CloseOutlined, DownloadOutlined
} from '@ant-design/icons';
import { exportToExcel } from '../../utils/excelExport';
import dayjs from 'dayjs';
import api from '../../services/api';
import BackButton from '../../components/BackButton';

const { Text } = Typography;

const AdminReviews = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('services');
  const [serviceReviews, setServiceReviews] = useState([]);
  const [productReviews, setProductReviews] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const [serviceRes, productRes] = await Promise.all([
        api.get('/services/admin/reviews/'),
        api.get('/products/admin/reviews/')
      ]);
      setServiceReviews(serviceRes.data.results || serviceRes.data);
      setProductReviews(productRes.data.results || productRes.data);
    } catch (error) {
      message.error(t('admin.reviews.loadFailed'));
    }
    setLoading(false);
  };

  const handleApproval = async (type, reviewId, approved) => {
    try {
      const endpoint = type === 'service'
        ? `/services/admin/reviews/${reviewId}/approval/`
        : `/products/admin/reviews/${reviewId}/approval/`;

      await api.patch(endpoint, { is_approved: approved });
      message.success(approved ? t('admin.reviews.approved') : t('admin.reviews.rejected'));
      fetchReviews();
    } catch (error) {
      message.error(t('admin.reviews.updateFailed'));
    }
  };

  const handleExport = (type) => {
    const data = type === 'service' ? serviceReviews : productReviews;
    const exportColumns = [
      { title: 'ID', dataIndex: 'id' },
      { title: t('admin.reviews.user'), dataIndex: 'user_email' },
      { title: t('admin.reviews.rating'), dataIndex: 'rating' },
      { title: t('admin.reviews.comment'), dataIndex: 'comment' },
      { title: t('admin.reviews.status'), dataIndex: 'is_approved', exportRender: (val) => val ? 'Approved' : 'Pending' },
      { title: t('admin.reviews.date'), dataIndex: 'created_at', exportRender: (date) => dayjs(date).format('YYYY-MM-DD') }
    ];
    exportToExcel(data, exportColumns, `${type}-reviews`, `${type.charAt(0).toUpperCase() + type.slice(1)} Reviews`);
    message.success(t('admin.reviews.exportSuccess'));
  };

  const getColumns = (type) => [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
    },
    {
      title: type === 'service' ? t('admin.reviews.service') : t('admin.reviews.product'),
      key: 'item',
      render: (_, record) => (
        <Text strong>
          {type === 'service'
            ? (record.service_name || record.service?.name_en || '-')
            : (record.product_name || record.product?.title || '-')
          }
        </Text>
      ),
    },
    {
      title: t('admin.reviews.user'),
      key: 'user',
      render: (_, record) => record.user_email || record.user?.email || '-',
    },
    {
      title: t('admin.reviews.rating'),
      dataIndex: 'rating',
      key: 'rating',
      width: 150,
      render: (rating) => <Rate disabled value={rating} />,
    },
    {
      title: t('admin.reviews.comment'),
      dataIndex: 'comment',
      key: 'comment',
      ellipsis: true,
      width: 300,
    },
    {
      title: t('admin.reviews.status'),
      dataIndex: 'is_approved',
      key: 'is_approved',
      width: 120,
      render: (isApproved) => (
        <Tag color={isApproved ? 'green' : 'orange'}>
          {isApproved ? t('admin.reviews.statusApproved') : t('admin.reviews.statusPending')}
        </Tag>
      ),
    },
    {
      title: t('admin.reviews.date'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          {!record.is_approved && (
            <Popconfirm
              title={t('admin.reviews.approveConfirm')}
              onConfirm={() => handleApproval(type, record.id, true)}
              okText={t('common.yes')}
              cancelText={t('common.no')}
            >
              <Button type="primary" size="small" icon={<CheckOutlined />}>
                {t('admin.reviews.approve')}
              </Button>
            </Popconfirm>
          )}
          {record.is_approved && (
            <Popconfirm
              title={t('admin.reviews.rejectConfirm')}
              onConfirm={() => handleApproval(type, record.id, false)}
              okText={t('common.yes')}
              cancelText={t('common.no')}
            >
              <Button size="small" danger icon={<CloseOutlined />}>
                {t('admin.reviews.reject')}
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'services',
      label: (
        <span>
          <StarOutlined /> {t('admin.reviews.serviceReviews')} ({serviceReviews.length})
        </span>
      ),
      children: (
        <>
          <div style={{ marginBottom: 16, textAlign: 'right' }}>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => handleExport('service')}
              disabled={!serviceReviews.length}
            >
              {t('admin.exportExcel')}
            </Button>
          </div>
          <Table
            columns={getColumns('service')}
            dataSource={serviceReviews}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 20 }}
          />
        </>
      ),
    },
    {
      key: 'products',
      label: (
        <span>
          <StarOutlined /> {t('admin.reviews.productReviews')} ({productReviews.length})
        </span>
      ),
      children: (
        <>
          <div style={{ marginBottom: 16, textAlign: 'right' }}>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => handleExport('product')}
              disabled={!productReviews.length}
            >
              {t('admin.exportExcel')}
            </Button>
          </div>
          <Table
            columns={getColumns('product')}
            dataSource={productReviews}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 20 }}
          />
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <BackButton to="/admin/dashboard" label={t('admin.backToDashboard')} />
      <Card
        title={
          <Space>
            <StarOutlined />
            <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
              {t('admin.reviews.title')}
            </span>
          </Space>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />
      </Card>
    </div>
  );
};

export default AdminReviews;
